import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { firestore, withActor, stripServerOwnedFields } from '../../backend'
import { collection, doc, updateDoc, query, where, getDoc, documentId, getDocs } from 'firebase/firestore'
import { getVenues, getVenuesGroup } from './venuesReducer'
import { getSageSaveVenue, saveTiersToSage, toSageRejection } from '../../utils/sage'

/**
 * Enregistre un contact. Une fiche déjà envoyée vers Sage passe par le callable
 * `sage_saveTiers` (« Sage d'abord ») : un refus de Sage rejette le thunk avec
 * `{ code, params, message }` dans `action.payload` et rien n'est écrit.
 */
export const saveClient = createAsyncThunk('clients/saveClient', async ({ id, data }, { getState, rejectWithValue }) => {
	// `metadata` / `sageSync` sont réservés au serveur : jamais réécrits depuis un brouillon.
	const payload = stripServerOwnedFields('clients', data)
	try {
		const state = getState()
		const sageVenue = getSageSaveVenue({ venues: getVenues(state), venuesGroup: getVenuesGroup(state), entity: data })
		if (sageVenue) return await saveTiersToSage({ venueId: sageVenue.id, clientId: id, data: payload })
		return await updateDoc(doc(firestore, 'clients', id), withActor(payload))
	} catch (error) {
		return rejectWithValue(toSageRejection(error))
	}
})

export const clientsSlice = createSlice({
	name: 'clients',
	initialState: {
		value: [],
		status: 'idle',
		error: null,
		selectedClient: {},
		isClientDetailModalOpen: false,
	},
	reducers: {
		setClientStatus: (state, action) => {
			state.status = action.payload
		},
		setSelectedClient: (state, action) => {
			state.selectedClient = action.payload
		},
		setIsClientDetailModalOpen: (state, action) => {
			state.isClientDetailModalOpen = action.payload
		},
	},
	extraReducers: {
		[saveClient.pending]: (state, action) => {
			state.status = 'loading'
		},
		[saveClient.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[saveClient.rejected]: (state, action) => {
			state.status = 'failed'
			// `rejectWithValue` porte { code, params, message } ; sinon l'erreur sérialisée
			state.error = action.payload || { code: action.error?.code, message: action.error?.message }
		},
	},
})

export const fetchClientsFromAccountId = async (accountId, venueId, venuesGroupId) => {
	const req = await getDocs(
		query(
			collection(firestore, 'clients'),
			where('account', '==', accountId),
			venuesGroupId ? where('venuesGroupId', '==', venuesGroupId) : where('etablissement', '==', venueId)
		)
	)
	return req.docs.map(doc => {
		const { lastReservationDate, ...rest } = doc.data()
		return { ...rest, id: doc.id }
	})
}

export const loadClientFromId = async clientId => {
	const docSnap = await getDoc(doc(firestore, 'clients', clientId))
	const { lastReservationDate, ...rest } = docSnap.data()
	return { ...rest, id: docSnap.id }
}

export const fetchClientsByBatches = clientsIds => {
	const batches = []
	for (let i = 0; i < clientsIds.length; i += 10) {
		const batch = clientsIds.slice(i, i + 10)
		const promise = getDocs(query(collection(firestore, 'clients'), where(documentId(), 'in', batch)))
		batches.push(promise)
	}

	return Promise.all(batches)
}

export const updateClient = (id, data) => {
	return updateDoc(doc(firestore, 'clients', id), withActor(stripServerOwnedFields('clients', data)))
}

export const getNewClientId = () => {
	return doc(collection(firestore, 'clients')).id
}

export const getClientStatus = state => state.clients.status
export const getSelectedClient = state => state.clients.selectedClient
export const getIsClientDetailModalOpen = state => state.clients.isClientDetailModalOpen

export const { setClientStatus, setSelectedClient, setIsClientDetailModalOpen } = clientsSlice.actions

export default clientsSlice.reducer
